<template>
  <div class="nomination-review" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header">{{ organizationDetails.programName }}</h1>
    </div>

    <div class="form-container nomination">
      <LoadSpinner v-if="loading" :color="`#${organizationDetails.darkBrandColor}`" />
      <template v-if="!loading">
        <h2>Nomination for {{nomination.organization}}</h2>
        <!-- Name of 3rd party user who submitted the nomination -->
        <h2 v-if="nominatedBy" v-translate="{ nominator: nominatedBy, subOrg: nominatorSubOrganization }">
          Nominated by %{nominator} (%{subOrg})
        </h2>


        <!-- Nomination survey response -->
        <div v-if="surveyResponse" class="survey card-shape">
          <div v-for="question in surveyResponse.questions" class="survey-question form-field">
            <div class="survey-question-text field-name">{{ question.text }}</div>
            <div class="survey-question-answer field-value">{{ question.answer.text }}</div>
          </div>
          <div class="form-footer">
            <div class="button-container">
              <!-- Deny -->
              <button
                class="form-button form-button-160 deny-button"
                @click="handleOpenConfirmationDialog('deny')"
                v-translate
              >
                Deny
              </button>

              <!-- Mark as incomplete -->
              <button
                class="form-button"
                @click="handleOpenConfirmationDialog('incomplete')"
                v-translate
              >
                Mark as Incomplete
              </button>

              <!-- Accept -->
              <button
                class="form-button accept-button"
                @click="handleOpenConfirmationDialog('accept')"
                v-translate
              >
                Advance Nominee
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Confirmation dialog -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
        overflow: 'hidden',
      }"
      v-if="isConfirmationDialogOpen"
      dimmed
    >
      <div class="form-container card-shape modal-window">
        <template v-if="action === 'accept'">
          <div class="form-field">
            <h2 class="form-field-header" v-translate>Confirm Advance</h2>
            <div class="form-field-container">
              <p v-translate>
                By advancing this nominee, they will be emailed login credentials so they can
                provide additional information for Canary to review before final approval. Are you
                sure you want to advance?
              </p>
            </div>
          </div>
          <div class="button-container" style="justify-content: space-between">
            <button @click="handleCloseConfirmationDialog(false)" class="form-button white-button">
              <span v-translate>Cancel</span>
            </button>

            <button @click="handleCloseConfirmationDialog(true)" class="form-button">
              <span v-translate>Advance Nominee</span>
            </button>
          </div>
        </template>
        <template v-else-if="action === 'incomplete'">
          <div class="form-field">
            <h2 class="form-field-header" v-translate>Confirm Mark as Incomplete</h2>
            <div class="form-field-container">
              <p v-translate>Please provide a feeback message to the nominator so they can revise the nomination:</p>
              <textarea class="form-field-textarea" v-model="message"></textarea>
            </div>
          </div>
          <div class="button-container" style="justify-content: space-between">
            <button @click="handleCloseConfirmationDialog(false)" class="form-button white-button">
              <span v-translate>Cancel</span>
            </button>

            <button @click="handleCloseConfirmationDialog(true)" class="form-button">
              <span v-translate>Mark as Incomplete</span>
            </button>
          </div>

        </template>
        <template v-else-if="action === 'deny'">
          <div class="form-field">
            <h2 class="form-field-header">Confirm Deny</h2>
            <div class="form-field-container">
              <p v-translate>Please provide a reason for this denial (your message will be included in the nominator's e-mail notification):</p>
              <textarea class="form-field-textarea" v-model="message"></textarea>
            </div>
          </div>
          <div class="button-container" style="justify-content: space-between">
            <button @click="handleCloseConfirmationDialog(false)" class="form-button white-button">
              <span v-translate>Cancel</span>
            </button>

            <button @click="handleCloseConfirmationDialog(true)" class="form-button">
              <span v-translate>Deny</span>
            </button>
          </div>
        </template>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { LoadSpinner } from '@/components'
import Modal from '@/components/Modal'

import NominationsApi from '@/services/nominations'
import SurveyResponseModel from '@/services/surveyresponses'

export default {
  name: 'NominationReview',
  components: {
    LoadSpinner,
    Modal,
  },
  data() {
    return {
      loading: true,
      nominationId: null,
      surveyResponse: null,
      nominatedBy: null,
      nominatorSubOrganization: null,
      nomination: null,
      isConfirmationDialogOpen: false,
      action: null,
      message: '',
    }
  },
  async mounted() {
    // Fetch the nomination, so we can look up the survey
    var nominationId = this.$route.params.id
    if (nominationId) {
      this.nominationId = nominationId
      var nomination = await NominationsApi.get(nominationId)

      if(nomination['organization]'] == "TCR Upward Fund") {
        this.nominatedBy = nomination['nominated_by']
        this.nominatorSubOrganization = nomination['nominator_sub_organization']
      }
    }

    // If we can't find the nomination, re-direct to the nominations admin page
    if (!nomination) {
      this.$router.push({ name: 'PartnersHome' })
    }

    // Load the survey response
    this.nomination = nomination
    this.getSurveyResponse(nomination['survey_id'])
    this.loading = false
  },
  methods: {
    getSurveyResponse(id) {
      const surveyResponsePromise = SurveyResponseModel.api
        .retrieve(id)
        .then(surveyResponse => {
          this.surveyResponse = surveyResponse
        })
        .catch(error => {
          try {
            error.response.status === 404 ? (this.errorCode = 404) : (this.errorCode = 500)
          } catch {
            this.errorCode = 500
          }
        })
      return surveyResponsePromise
    },
    async handleDenyNomination() {
      await NominationsApi.updateStatus(this.nominationId, 'DENIED', this.message)
      this.$router.push({ name: 'StaffNominationsOverview' , params: { nomineeDenied: true } })
    },
    async handleAcceptNomination() {
      await NominationsApi.updateStatus(this.nominationId, 'ACCEPTED')
      this.$router.push({ name: 'StaffNominationsOverview' , params: { nomineeAdvanced: true } })
    },
    async handleNominationIncomplete() {
      await NominationsApi.updateStatus(this.nominationId, 'INCOMPLETE', this.message)
      this.$router.push({ name: 'StaffNominationsOverview' , params: { nominationIncomplete: true } })
    },
    handleOpenConfirmationDialog(action) {
      this.action = action
      this.isConfirmationDialogOpen = true
    },
    handleCloseConfirmationDialog(confirmedAction) {
      if (confirmedAction) {
        if (this.action === 'deny') {
          this.handleDenyNomination()
        } else if (this.action === 'incomplete') {
          this.handleNominationIncomplete()
        } else if (this.action === 'accept') {
          this.handleAcceptNomination()
        }
      }

      this.action = null
      this.isConfirmationDialogOpen = false
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars']),
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.nomination-review {
  .survey {
    max-width: 800px;
    align-self: center;
    padding: 2rem;
  }

  .deny-button {
    background-color: red;
  }

  .accept-button {
    background-color: green;
  }
}

.nomination {
  align-items: center;
}
</style>
